import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientConstants,
  LocalStorageKeys,
  LocalStorageService,
  LoggedInUserService,
  NotificationService,
  SessionTestService
} from '@formbird/services';
import { SharedUrlRoutes, UtilValidation } from '@formbird/shared';

import { CustomRouteService } from '@services/custom-route.service';
@Component({
  selector: 'ft-public-login',
  template: `@if(loading) {
    <ft-loading></ft-loading>
  }`,
  standalone: true
})
export class PublicLoginComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loggedInUserService = inject(LoggedInUserService);
  private notificationService = inject(NotificationService);
  private sessionTestService = inject(SessionTestService);
  private localStorageService = inject(LocalStorageService);
  private customRouteService = inject(CustomRouteService);

  sub: any;
  user: any;
  loading: boolean = true;

  private async authenticatePublicUser(accountPublicURL) {
    const { publicLogin } = SharedUrlRoutes.serverRoutes;
    try {
      const response: any = await this.http.post(publicLogin,
        { context: accountPublicURL }).toPromise();
      const data = response;
      if (data.status === 'ok') {
        this.loggedInUserService.setUser(data.user);
        await this.sessionTestService.testSession();
        return data.user;
      } else {
        throw new Error('User not found!');
      }
    } catch (err) {
      console.log(err);
      if (err?.message?.includes('Unknown Error') || err?.message?.includes('Network Error')) {
        throw err;
      } else {
        throw new Error('Public login failed because the account is not public');
      }
    }
  }

  private async checkPublicUser(accountPublicURL) {
    try {
      this.localStorageService.setItem(LocalStorageKeys.CHECK_PUBLIC_USER, "true");
      this.user = await this.loggedInUserService.loadLoggedUser();
      if (this.user.accountPublicURL === accountPublicURL) {
        return this.user;
      } else {
        await this.authenticatePublicUser(accountPublicURL);
      }
    } catch (err) {
      await this.authenticatePublicUser(accountPublicURL);
    } finally {
      this.localStorageService.removeItem(LocalStorageKeys.CHECK_PUBLIC_USER);
      this.localStorageService.removeItem(ClientConstants.LAST_ENTERED_STATE_URL);
    }
  }

  async ngOnInit() {
    const _self = this;
    this.sub = this.route.params.subscribe(async (params: any) => {
      const {
        accountPublicURL,
        paramRoute,
        paramQuery,
        override
      } = params;

      const navExtras = { skipLocationChange: true, replaceUrl: false };

      if (accountPublicURL && accountPublicURL !== '') {
        const firstRoute = accountPublicURL;
        const authRoutes = {
          'login': '/auth/login',
          'loginSaml': '/auth/login/saml',
          'forgotpass': '/auth/forgotpass',
          'signup': '/auth/signup',
          'resetpass': '/auth/resetpass',
          'activateAccount': '/auth/activateAccount'
        };

        if (Object.keys(authRoutes).indexOf(firstRoute) >= 0) {
          this.router.navigate([authRoutes[firstRoute]], { ...navExtras, queryParamsHandling: 'preserve' });
        } else if (firstRoute === 'searchNoNavBar') {
          this.router.navigate(['/search/noNavBar', paramRoute], navExtras);
        } else {
          try {
            await this.checkPublicUser(accountPublicURL);
            if (paramRoute === 'form' && override && override !== '') {
              this.router.navigate(['/form', paramQuery, override], navExtras);
            } else if (paramRoute === 'form') {
              this.router.navigate(['/form', paramQuery], navExtras);
            } else if (paramRoute === 'formNoNavBar') {
              this.router.navigate(['/formNoNavBar', paramQuery], navExtras);
            } else if (paramRoute === 'search') {
              this.router.navigate(['/search', paramQuery], navExtras);
            } else if (paramRoute === 'searchNoNavBar') {
              this.router.navigate(['/search/noNavBar', paramQuery], navExtras);
            } else if (paramRoute === 'external') {
              this.router.navigate(['/external?p=', paramQuery], navExtras);
            } else {
              if (this.user.accountPublicURL !== accountPublicURL) {
                const customRoute: any = await this.customRouteService.getURLRoute(accountPublicURL);
                const routeDoc = customRoute?.urlRouteTemplateRel?.[0];
                if (routeDoc) {
                  const customFunc = routeDoc.routeHandler;
                  let routes;
                  if (UtilValidation.hasValue(customFunc)) {
                    const routeHandler = Function(`return (${customFunc})`)();
                    routes = await Promise.resolve(routeHandler(paramRoute));
                  } else {
                    routes = paramRoute ? ['/form', paramRoute, routeDoc.documentId] : ['/form', routeDoc.documentId];
                  }

                  if (customRoute.titlePrefix) {
                    this.localStorageService.setItem(LocalStorageKeys.TITLE_PREFIX, customRoute.titlePrefix);
                  }
                  // don't clutter history. when back if pressed on current route, it'll go to the what's specified in the routeDoc
                  // causing a navigation back to the page where the back button was pressed
                  this.router.navigate(routes, { ...navExtras, replaceUrl: true });
                } else {
                  throw new Error("Invalid urlRoute document: " + customRoute.documentId);
                }
              } else {
                this.router.navigate(['/default'], navExtras);
              }
            }
            _self.loading = false;
          } catch (err) {
            if (paramRoute === 'login') {   // from a disabled local login
              this.router.navigate(['/notFound'], {
                ...navExtras,
                queryParams: {
                  title: 'Account Disabled',
                  message: 'Your account is currently disabled. Please contact a system administrator to verify your account'

                }
              });
            } else if (err.message?.includes('Unknown Error') || err.message?.includes('Network Error')) {
              this.router.navigate(['/notFound'], {
                ...navExtras,
                queryParams: {
                  title: 'Network Connection Lost',
                  message: 'Sorry, the network connection has been lost and caching has not been enabled.'

                }
              });
            } else {
              this.notificationService.error(err.message);
              this.router.navigate(['/notFound'], navExtras);
            }
            _self.loading = false;
          }
        }
      }

    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

}
